import { wrapRequest } from '@applandeo/api-wrapper';
import { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import { absenceDeletionRequest } from '@/dataExchange/services/requestsPage.service';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import { RequestDetailsAcceptanceContext } from '@/pages/Calendar/context/requestDetailsAcceptanceContext';
import useAcceptDeletionLogic from '@/pages/Requests/common/DeletRequestButton/useAcceptDeletionLogic';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

interface IDeleteRequestButtonProps {
    className?: string;
    absenceId: string;
    absenceStatus: REQUEST_STATUS;
    onCloseModal?: () => void;
    getRequests: () => void;
    isOwnRequest?: boolean;
    requestName: string;
    hasPermissionForAcceptance?: boolean;
}

const DeleteRequestButton = ({
    className,
    absenceId,
    absenceStatus,
    getRequests,
    isOwnRequest,
    onCloseModal,
    requestName,
    hasPermissionForAcceptance,
}: IDeleteRequestButtonProps) => {
    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { isAdmin, isLead, isCompanyOwner } = useLoggedInUserData();
    const { triggerCalendarUpdate } = useContext(RequestDetailsAcceptanceContext);

    const AbsenceDeletionRequestWrapper = wrapRequest(absenceDeletionRequest);

    const { acceptDelete, isActionLoading } = useAcceptDeletionLogic({
        absenceId,
        onCloseModal,
        getRequests,
    });

    const isAccepted = absenceStatus === REQUEST_STATUS.ACCEPTED;

    const onDelete = () => {
        if (!absenceId) return;

        AbsenceDeletionRequestWrapper.fetch(absenceId)
            .onSuccess(() => {
                if (isOwnRequest && isLead && isAccepted && hasPermissionForAcceptance) {
                    acceptDelete();
                    return;
                }

                createAlert(
                    ALERT_VARIANT.success,
                    !isAccepted || isAdmin || isLead || isCompanyOwner
                        ? t('requests_alerts_deletion_request_success')
                        : t('notifications_message_items_you_ask_for_removal')
                );
                mixpanelTracking(MIXPANEL_ACTIONS.DELETED_REQUEST, { absenceName: requestName });
                getRequests();
                onCloseModal?.();
                triggerCalendarUpdate();
            })
            .onLocalError(() => {
                createAlert(ALERT_VARIANT.error, t('requests_alerts_deletion_request_failed'));
            });
    };

    return (
        <FQButton
            className={className}
            data-variant={FQ_BUTTON_VARIANTS.fqOutline}
            color="fqRed"
            onClick={onDelete}
            disabled={AbsenceDeletionRequestWrapper.isLoading || isActionLoading}
        >
            {t('teamDetailsView_deleteAlert_title')}
        </FQButton>
    );
};

const Memoized = memo(DeleteRequestButton);
const Styled = styled(Memoized)``;

export default Styled;
